import { getSessionId, HANDOFF_BASE_URL } from '@onefootprint/dev-tools';
import type { OnboardingSessionConfig } from '@onefootprint/request-types';
import { useMemo } from 'react';
import Logger from '@/idv2/utils/logger';
import type { TransferRequirements } from '../types';
type UseCreateHandoffUrlParams = {
  authToken?: string;
  language?: string;
  missingRequirements: TransferRequirements;
  onboardingConfig: OnboardingSessionConfig;
  fromContinueOnMobile: boolean;
};

// Creates a handoff url (with auth token, session info, and sdk version for tracking) for mobile to complete verification
const useCreateHandoffUrl = ({
  authToken,
  language = 'en',
  missingRequirements,
  onboardingConfig,
  fromContinueOnMobile
}: UseCreateHandoffUrlParams): string | undefined => useMemo(() => {
  if (!authToken) {
    return undefined;
  }
  const {
    isAppClipEnabled,
    appClipExperienceId
  } = onboardingConfig;
  const newUrl = new URL(HANDOFF_BASE_URL);
  if (isAppClipEnabled) {
    newUrl.pathname = `appclip/${appClipExperienceId}`;
  }
  const params = new URLSearchParams();
  const fpSessionId = getSessionId();
  const randomSeed = Math.floor(Math.random() * 1000).toString();
  const loggerGlobalContext = Logger.getGlobalContext();
  if (language !== 'en') {
    params.append('lng', language);
  }
  if (fpSessionId) {
    params.append('xfpsessionid', fpSessionId);
  }
  if (fromContinueOnMobile) {
    params.append('userPrefersMobileCapture', 'true');
  }
  if (loggerGlobalContext?.client_version) {
    params.append('sdkv', String(loggerGlobalContext.client_version));
  }
  params.append('r', randomSeed);
  params.append('authToken', authToken);
  newUrl.search = params.toString();
  newUrl.hash = encodeURI(authToken);
  return newUrl.toString();
}, [authToken, onboardingConfig, language, missingRequirements]);
export default useCreateHandoffUrl;